// Skuret's local store. One IndexedDB object store "items" keyed by id; every
// record belongs to an owner (the account's user_id, guests included), so two
// accounts on one phone never see each other's recipes, notes or lists.
//
// Deleting leaves a tombstone ({deleted: true, data: {}}) so an optional sync
// can pass the deletion on; list/get never return tombstones.
// When IndexedDB cannot be opened (private browsing, full disk, old WebView)
// the same interface runs in memory and `persistent` is false, so the UI can
// say plainly that nothing survives closing the app.

const DB_VERSION = 1;
let lastStamp = 0;

// Strictly increasing milliseconds: two saves in the same millisecond must
// still order correctly for "newest wins".
export function stamp() {
  lastStamp = Math.max(Date.now(), lastStamp + 1);
  return lastStamp;
}

export function newId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function makeApi(raw) {
  // raw: { read(id), write(record), byOwner(owner) } all async
  const api = {
    async list(owner, kind) {
      const rows = await raw.byOwner(owner);
      return rows.filter(r => !r.deleted && (!kind || r.kind === kind))
        .sort((a, b) => b.updatedAt - a.updatedAt);
    },
    async get(owner, id) {
      const row = await raw.read(id);
      return row && row.owner === owner && !row.deleted ? row : null;
    },
    async put(owner, kind, data, { id } = {}) {
      const existing = id ? await raw.read(id) : null;
      if (existing && existing.owner !== owner) throw new Error('shed_owner_mismatch');
      // Newer than the version it replaces even when that came from a device
      // whose clock runs ahead; otherwise the edit could never win a sync.
      const now = Math.max(stamp(), (existing?.updatedAt || 0) + 1);
      const record = {
        id: existing?.id || id || newId(), owner, kind: existing?.kind || kind, data,
        createdAt: existing?.createdAt || now, updatedAt: now, deleted: false,
      };
      // The version this edit was made from, which the optional sync sends as its base.
      if (existing?.syncedAt !== undefined) record.syncedAt = existing.syncedAt;
      await raw.write(record);
      return record;
    },
    async remove(owner, id) {
      const existing = await raw.read(id);
      if (!existing || existing.owner !== owner || existing.deleted) return null;
      const tomb = { ...existing, data: {}, deleted: true, updatedAt: Math.max(stamp(), existing.updatedAt + 1) };
      await raw.write(tomb);
      return tomb;
    },
    async all(owner) { return raw.byOwner(owner); },
    async putRaw(record) { await raw.write(record); return record; },
  };
  return api;
}

export function createMemoryStore() {
  const rows = new Map();
  const copy = value => (value ? JSON.parse(JSON.stringify(value)) : null);
  return makeApi({
    async read(id) { return copy(rows.get(id)); },
    async write(record) { rows.set(record.id, copy(record)); },
    async byOwner(owner) { return [...rows.values()].filter(r => r.owner === owner).map(copy); },
  });
}

function request(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function createIdbStore(idb, name = 'growdaily-shed') {
  const open = idb.open(name, DB_VERSION);
  open.onupgradeneeded = () => {
    const db = open.result;
    if (!db.objectStoreNames.contains('items')) {
      const items = db.createObjectStore('items', { keyPath: 'id' });
      items.createIndex('owner', 'owner');
    }
  };
  const db = await request(open);
  const tx = mode => db.transaction('items', mode).objectStore('items');
  return makeApi({
    async read(id) { return (await request(tx('readonly').get(id))) || null; },
    async write(record) { await request(tx('readwrite').put(record)); },
    async byOwner(owner) { return request(tx('readonly').index('owner').getAll(owner)); },
  });
}

export async function openShedStore(env = globalThis, timeoutMs = 4000) {
  try {
    if (!env.indexedDB) throw new Error('no indexedDB');
    // Some WebViews never answer open(); do not leave the Shed on "loading".
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('indexedDB timeout')), timeoutMs));
    return { store: await Promise.race([createIdbStore(env.indexedDB), timeout]), persistent: true };
  } catch {
    return { store: createMemoryStore(), persistent: false };
  }
}
